import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Shield, Key, Bell, Lock } from 'lucide-react';

const CustomerSettings = () => {
  const { user, toggle2FA } = useAuth();
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(user?.two_factor_enabled || false);
  const [loading, setLoading] = useState(false);

  const handle2FAToggle = async (enabled) => {
    setLoading(true);
    try {
      await toggle2FA(enabled);
      setTwoFactorEnabled(enabled);
      toast.success(`Two-factor authentication ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update 2FA settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link to="/portal/dashboard" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-5 h-5" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl font-bold text-navy font-serif mb-2">Settings</h1>
          <p className="text-slate-600 mb-8">Manage your account security and preferences.</p>

          {/* Security Settings */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-navy">Security</h2>
                <p className="text-sm text-slate-500">Protect your account</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* 2FA Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-4">
                  <Key className="w-5 h-5 text-slate-500" />
                  <div>
                    <Label className="text-sm font-medium">Two-Factor Authentication</Label>
                    <p className="text-xs text-slate-500">Add an extra layer of security with email OTP</p>
                  </div>
                </div>
                <Switch
                  checked={twoFactorEnabled}
                  onCheckedChange={handle2FAToggle}
                  disabled={loading}
                  data-testid="2fa-toggle"
                />
              </div>

              {/* Password Change */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-4">
                  <Lock className="w-5 h-5 text-slate-500" />
                  <div>
                    <Label className="text-sm font-medium">Change Password</Label>
                    <p className="text-xs text-slate-500">Update your account password</p>
                  </div>
                </div>
                <Link to="/forgot-password">
                  <Button variant="outline" size="sm" className="rounded-full">
                    Change
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Account Info */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <h2 className="text-lg font-semibold text-navy mb-4">Account Information</h2>
            <dl className="space-y-4">
              <div className="flex justify-between py-2 border-b border-slate-100">
                <dt className="text-slate-500">Name</dt>
                <dd className="font-medium">{user?.name}</dd>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <dt className="text-slate-500">Email</dt>
                <dd className="font-medium">{user?.email}</dd>
              </div>
              <div className="flex justify-between py-2">
                <dt className="text-slate-500">Account Type</dt>
                <dd className="font-medium capitalize">{user?.role}</dd>
              </div>
            </dl>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CustomerSettings;
